import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Zap, CalendarDays } from 'lucide-react';
import { toast } from 'react-toastify';
import { cartService } from '../../services/cartService';
import catalogService from '../../services/catalogService'; 
import { generateSlug } from '../../utils/helpers';
import { PRODUCT_STATUS, PRODUCT_STATUS_CONFIG } from '../../utils/constants';
import { useCart } from '../../contexts/CartContext';

let cachedConfigPromise = null;
const fetchGlobalConfig = () => {
    if (!cachedConfigPromise) {
        cachedConfigPromise = catalogService.getSystemConfig().catch(() => null);
    }
    return cachedConfigPromise;
};

const stripHtml = (html) => {
    if (!html) return '';
    return html
        .replace(/<\/(p|div|li|h[1-6])>/gi, ' ') 
        .replace(/<br\s*\/?>/gi, ' ') 
        .replace(/<[^>]*>?/gm, '') 
        .replace(/&nbsp;/g, ' ') 
        .replace(/\s+/g, ' ') 
        .trim();
};

const FlashSaleCountdown = memo(({ endTime, onExpire }) => {
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!endTime) return;
        const timer = setInterval(() => {
            const distance = endTime - new Date().getTime();
            if (distance <= 0) {
                clearInterval(timer);
                if (onExpire) onExpire();
            } else {
                setTimeLeft({
                    hours: String(Math.floor(distance / (1000 * 60 * 60))).padStart(2, '0'),
                    minutes: String(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0'),
                    seconds: String(Math.floor((distance % (1000 * 60)) / 1000)).padStart(2, '0')
                });
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [endTime, onExpire]);

    if (!timeLeft) return null;

    return (
        <div className="flex items-center gap-0.5 text-[9px] font-mono text-white">
            <span className="bg-tnc-orange px-1 rounded-sm">{timeLeft.hours}</span>:
            <span className="bg-tnc-orange px-1 rounded-sm">{timeLeft.minutes}</span>:
            <span className="bg-tnc-orange px-1 rounded-sm">{timeLeft.seconds}</span>
        </div>
    );
});

// 👉 TỐI ƯU 1: Bổ sung prop 'priority' để nhận tín hiệu Load nhanh từ Component cha
const ProductCard = memo(({ product, getImageUrl, adminSpecs = [], priority = false }) => {
    const navigate = useNavigate(); 
    const { fetchCart } = useCart();

    const [flashSaleEndTime, setFlashSaleEndTime] = useState(null);

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
    
    const mainImage = useMemo(() => {
        return product.images?.find(img => img.isMain)?.itemImageUrl 
            || product.images?.[0]?.itemImageUrl 
            || product.itemImage;
    }, [product.images, product.itemImage]);

    // 👉 TỐI ƯU 2: Cố định thông tin Giá Flash Sale ngay từ đầu để chống CLS
    // Không phụ thuộc vào API fetchGlobalConfig nữa. Backend đã validate giá này trước khi trả về.
    const { isContactPrice, isFlashSale, currentPrice, originalPrice, minRentalPrice } = useMemo(() => {
        const contactPrice = !product.itemReleasePrice || product.itemReleasePrice <= 0;
        // Chốt luôn FlashSale nếu Backend trả về có giá và số lượng FlashSale > 0
        const flashSale = !contactPrice && product.itemFlashSalePrice > 0 && product.itemFlashSaleQuantity > 0 && !product.isForRent; 
        const curr = flashSale ? product.itemFlashSalePrice : (product.itemSalePrice || product.itemReleasePrice);
        const orig = product.itemReleasePrice;

        let minRent = null;
        if (product.isForRent && product.rentalPrices?.length > 0) {
            const prices = product.rentalPrices.map(r => r.price).filter(p => p > 0);
            if (prices.length > 0) minRent = Math.min(...prices);
        }

        return {
            isContactPrice: contactPrice,
            isFlashSale: flashSale,
            currentPrice: curr,
            originalPrice: orig,
            minRentalPrice: minRent
        };
    }, [product.itemReleasePrice, product.itemSalePrice, product.itemFlashSalePrice, product.itemFlashSaleQuantity, product.isForRent, product.rentalPrices]);

    // API này bây giờ CHỈ dùng để lấy Đồng hồ đếm ngược, không can thiệp vào logic tính giá nữa
    useEffect(() => {
        if (!isFlashSale) return;

        let isMounted = true;
        fetchGlobalConfig().then(res => {
            if (!isMounted) return;
            const actualConfig = res?.data || res;
            if (actualConfig && actualConfig.flash_sale_end) {
                setFlashSaleEndTime(new Date(actualConfig.flash_sale_end).getTime());
            }
        });

        return () => { isMounted = false; };
    }, [isFlashSale]);

    const handleFlashSaleExpire = useCallback(() => {
        setFlashSaleEndTime(null);
    }, []);

    const importantSpecs = useMemo(() => {
        if (!product.itemSpecs || product.itemSpecs.length === 0 || !adminSpecs || adminSpecs.length === 0) return [];

        const filterableSpecNames = new Set(
            adminSpecs
                .filter(a => a.isFilterable && String(a.deviceCategory?.deviceCategoryId) === String(product.categoryId))
                .map(a => a.specName?.trim().toLowerCase())
        );

        if (filterableSpecNames.size === 0) return [];

        const results = [];
        for (const spec of product.itemSpecs) {
            const specName = spec.spec?.specName?.trim();
            if (!specName) continue;

            if (filterableSpecNames.has(specName.toLowerCase())) {
                const cleanTextValue = stripHtml(spec.specValue || "").trim();
                if (cleanTextValue) {
                    results.push({ name: specName, cleanValue: cleanTextValue });
                    if (results.length >= 5) break; 
                }
            }
        }
        return results;
    }, [product.itemSpecs, product.categoryId, adminSpecs]);

    const handleAddToCart = async (e) => {
        e.preventDefault(); 
        e.stopPropagation(); 
        try {
            await cartService.addToCart(product.itemId, 1);
            toast.success("Đã thêm sản phẩm vào giỏ hàng! 🛒");
            await fetchCart(); 
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                toast.error("Vui lòng đăng nhập để mua hàng!");
                navigate('/login');
            } else {
                toast.error(error.response?.data || "Lỗi khi thêm vào giỏ hàng!");
            }
        }
    };

    const renderStatus = () => {
        const status = Number(product.itemStatus); 
        const config = PRODUCT_STATUS_CONFIG[status] || PRODUCT_STATUS_CONFIG[PRODUCT_STATUS.IN_STOCK];
        let displayText = config.text;

        if (status === PRODUCT_STATUS.PRE_ORDER) {
            displayText = 'Có hàng';
        }

        return (
            <span className={`text-[10px] font-bold uppercase ${config.color}`}>
                {displayText}
            </span>
        );
    };

    const productUrl = `/san-pham/${generateSlug(product.itemName)}-id${product.itemId}`;

    return (
        <div 
            className={`bg-white rounded-xl p-2.5 transition-all duration-300 group flex flex-col relative w-full h-full border border-orange-400/60 hover:-translate-y-1 hover:z-10 shadow-sm hover:shadow-xl hover:shadow-orange-200/60 ${
                isFlashSale ? 'shadow-[0_4px_15px_rgba(255,85,0,0.15)]' : ''
            }`}
        >
            {product.isForRent && (
                <div className="absolute top-2 left-2 z-10 bg-tnc-orange text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded flex items-center gap-1 shadow-sm uppercase tracking-wider">
                    <CalendarDays size={10} /> Cho Thuê
                </div>
            )}

            <div className={`flex items-center justify-between mb-2 min-h-[16px] ${product.isForRent ? 'mt-7' : 'mt-1'}`}>
                {product.brand && (
                    product.brand.brandImageUrl ? (
                        // 👉 TỐI ƯU 3: Giữ layout cho ảnh Brand bằng width/height và aspect-ratio
                        <img 
                            src={getImageUrl ? getImageUrl(product.brand.brandImageUrl) : product.brand.brandImageUrl} 
                            alt={product.brand.brandName} 
                            width={80}
                            height={16}
                            loading="lazy"
                            decoding="async"
                            className="h-3.5 md:h-4 w-auto object-contain shrink-0 max-w-[80px]" 
                            title={product.brand.brandName} 
                        />
                    ) : (
                        <span className="text-[10px] font-bold text-gray-500 uppercase shrink-0">{product.brand.brandName}</span>
                    )
                )}
                
                <div className="text-[9px] md:text-[10px] text-blue-800 font-mono truncate ml-auto">
                    {product.itemCode || product.itemId}
                </div>
            </div>

            <Link to={productUrl} className="block w-full">
                {/* Khung ảnh giữ nguyên aspect-ratio bằng pb-[100%] */}
                <div className="relative w-full pb-[100%] mb-3 overflow-hidden rounded-lg bg-white shrink-0">
                    {/* 👉 TỐI ƯU 4 LCP: Đón nhận thẻ priority để ép Load sớm bức ảnh của sản phẩm đầu tiên */}
                    <img 
                        src={getImageUrl ? getImageUrl(mainImage) : mainImage} 
                        alt={product.itemName} 
                        width={400}
                        height={400}
                        loading={priority ? "eager" : "lazy"}
                        fetchPriority={priority ? "high" : "auto"}
                        decoding={priority ? "sync" : "async"}
                        className="absolute inset-0 w-full h-full object-contain p-1 group-hover:scale-125 transition-transform duration-500"
                    />
                </div>

                <div className="text-[13px] md:text-sm font-bold text-gray-800 group-hover:text-tnc-orange line-clamp-3 leading-snug mb-1 min-h-[54px] md:min-h-[60px]" title={product.itemName}>
                    {product.itemName}
                </div>
            </Link>

            <div className="flex flex-col flex-1">
                {importantSpecs.length > 0 && (
                    <div className="flex flex-wrap content-start gap-1.5 w-full max-h-[74px] overflow-hidden mb-2">
                        {importantSpecs.map((spec, idx) => (
                            <div 
                                key={idx} 
                                className="bg-orange-50/50 border border-orange-100 text-gray-700 text-[10px] px-2 py-0.5 rounded-md flex items-center max-w-full"
                                title={spec.cleanValue}
                            >
                                <span className="truncate">{spec.cleanValue}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-auto flex flex-col justify-end pt-1 min-h-[38px]">
                    {isFlashSale && flashSaleEndTime && (
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-0.5 text-[10px] font-bold uppercase text-tnc-orange">
                                <Zap fill="currentColor" size={12}/> DEAL SỐC
                            </div>
                            <FlashSaleCountdown endTime={flashSaleEndTime} onExpire={handleFlashSaleExpire} />
                        </div>
                    )}

                    {product.isForRent ? (
                        <div className="flex flex-col">
                            <span className="text-[10px] text-gray-500 font-bold mb-0.5">Giá thuê chỉ từ:</span>
                            <div className="font-extrabold text-[15px] md:text-lg leading-none text-tnc-orange-dark">
                                {minRentalPrice ? `${formatPrice(minRentalPrice)}` : 'Liên Hệ Báo Giá'}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={`font-extrabold text-[15px] md:text-lg leading-none ${isContactPrice ? 'text-blue-800' : 'text-tnc-orange'}`}>
                                {isContactPrice ? 'Giá Liên Hệ' : formatPrice(currentPrice)}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 h-3">
                                {(!isContactPrice && originalPrice && currentPrice < originalPrice) && (
                                    <div className="text-[10px] md:text-[11px] text-gray-400 line-through leading-none">{formatPrice(originalPrice)}</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="mt-3 pt-3 border-t flex items-center justify-between shrink-0 border-orange-100/60 relative z-20 h-[44px]">
                <div className="px-2 py-1 rounded-sm bg-white">
                    {renderStatus()}
                </div>
                
                {(!product.isForRent && (Number(product.itemStatus) === 2 || Number(product.itemStatus) === 3) && !isContactPrice) && (
                    <button 
                        onClick={handleAddToCart}
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors bg-orange-100 text-tnc-orange hover:bg-tnc-orange hover:text-white cursor-pointer"
                        title="Thêm vào giỏ"
                    >
                        <ShoppingCart size={14} />
                    </button>
                )}
            </div>
        </div>
    );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;